<div class="checkout-shipping">
    <div class="space-y-6" *ngIf="(signedIn$ | async) === false">
        <h2 class="text-lg font-medium text-gray-900">Contact information</h2>
        <form [formGroup]="contactForm" (focusout)="onCustomerFormBlur()">
            <div class="mt-4 text-left">
                <label
                    for="emailAddress"
                    class="checkout-shipping__label"
                >
                    Email address
                </label>
                <div class="mt-1">
                    <input
                        type="email"
                        id="emailAddress"
                        name="emailAddress"
                        autoComplete="email"
                        formControlName="emailAddress"
                        class="checkout-shipping__input"
                        aria-label="Email address"
                    />
                </div>
            </div>
            <div class="checkout-shipping__form-group--grid">
                <div>
                    <label
                        for="firstName"
                        class="checkout-shipping__label"
                    >
                        First name
                    </label>
                    <div class="mt-1">
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            autoComplete="given-name"
                            formControlName="firstName"
                            class="checkout-shipping__input"
                            aria-label="First name"
                        />
                    </div>
                </div>

                <div>
                    <label
                        for="lastName"
                        class="checkout-shipping__label"
                    >
                        Last name
                    </label>
                    <div class="mt-1">
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            autoComplete="family-name"
                            formControlName="lastName"
                            class="checkout-shipping__input"
                            aria-label="Last name"
                        />
                    </div>
                </div>
            </div>
        </form>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6" *ngIf="step === Step.Address">
        <div class="checkout-shipping__card">
            <div class="checkout-shipping__header">
                <h2 class="text-[18px] font-semibold text-black">Billing Address</h2>
            </div>
            <div class="flex-grow">
                <ng-container *ngIf="billingAddress$ | async as billing; else noBilling">
                    <div class="space-y-1 text-gray-600 text-[15px]">
                        <p class="font-medium text-black">{{ billing.fullName }}</p>
                        <p *ngIf="billing.company">{{ billing.company }}</p>
                        <p>{{ billing.streetLine1 }}</p>
                        <p *ngIf="billing.streetLine2">{{ billing.streetLine2 }}</p>
                        <p>{{ billing.city }}, {{ billing.postalCode }}</p>
                        <p>{{ billing.country }}</p>
                    </div>
                </ng-container>
                <ng-template #noBilling>
                    <p class="text-gray-400 italic">No billing address set.</p>
                </ng-template>
            </div>
            <div class="checkout-shipping__footer">
                <p class="text-[13px] text-gray-500">
                    To change billing address, 
                    <a [href]="supportMailto" class="hover:underline text-[var(--color-brand-primary)]">Contact us via email</a>
                </p>
            </div>
        </div>

        <div class="checkout-shipping__card">
            <div class="checkout-shipping__header">
                <h2 class="text-[18px] font-semibold text-black">Shipping Address</h2>
                <button *ngIf="signedIn$ | async" 
                        class="checkout-shipping__button"
                        (click)="step = Step.SelectAddress"
                        aria-label="Change shipping address">
                    Change
                </button>
            </div>
            <div class="flex-grow">
                <ng-container *ngIf="shippingAddress$ | async as shipping; else noShipping">
                    <div class="space-y-1 text-gray-600 text-[15px]">
                        <p class="font-medium text-black">{{ shipping.fullName }}</p>
                        <p *ngIf="shipping.company">{{ shipping.company }}</p>
                        <p>{{ shipping.streetLine1 }}</p>
                        <p *ngIf="shipping.streetLine2">{{ shipping.streetLine2 }}</p>
                        <p>{{ shipping.city }}, {{ shipping.postalCode }}</p>
                        <p>{{ shipping.country }}</p>
                    </div>
                </ng-container>
                <ng-template #noShipping>
                    <p class="text-gray-400 italic">No shipping address set.</p>
                </ng-template>
            </div>
            <div class="checkout-shipping__footer" *ngIf="(signedIn$ | async) === false">
                 <p class="text-[13px] text-gray-500">Fill in your shipping details below.</p>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 rounded-[16px] border border-[var(--color-border-subtle)]" *ngIf="step === Step.SelectAddress">
        <div class="mb-6 flex items-center justify-between gap-4">
            <h2 class="text-[20px] font-semibold text-black">Select Shipping Address</h2>
            <button type="button" class="checkout-shipping__button--close shrink-0" (click)="step = Step.Address" aria-label="Close address selection">
                <img src="assets/icons/close-icon.svg" alt="" class="w-6 h-6">
            </button>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div *ngFor="let address of customerAddresses$ | async"
                 (click)="setShippingAddress(address)"
                 (keydown.enter)="setShippingAddress(address)"
                 (keydown.space)="setShippingAddress(address); $event.preventDefault()"
                 tabindex="0"
                 role="button"
                 class="checkout-shipping__card--selection"
                 [ngClass]="(shippingAddress$ | async)?.id === address.id ? 'border-primary-600 bg-primary-50' : 'border-gray-100 hover:border-gray-200 bg-white'">
                <div class="space-y-1 text-sm text-gray-600">
                    <p class="font-semibold text-black mb-2">{{ address.fullName }}</p>
                    <p *ngFor="let line of getLines(address)">{{ line }}</p>
                </div>
            </div>
            
            <button (click)="createAddress()"
                    class="checkout-shipping__card--add-new group"
                    aria-label="Add new shipping address">
                <img src="assets/icons/plus-icon.svg" alt="Add" class="w-8 h-8 mb-2 group-hover:scale-110 transition-transform">
                <span class="font-medium">Add New Address</span>
            </button>
        </div>

        <div class="mt-8 pt-6 border-t border-gray-100 flex justify-between">
            <button (click)="step = Step.Address" class="text-gray-600 hover:text-black font-medium" aria-label="Back to shipping overview">
                Back to overview
            </button>
        </div>
    </div>

    <div class="bg-white p-6 rounded-[16px] border border-[var(--color-border-subtle)]" *ngIf="step === Step.Address && ((signedIn$ | async) === false || (shippingAddress$ | async) === null)">
        <h2 class="text-[18px] font-semibold text-black mb-4">Edit Shipping Details</h2>
        <vsf-address-form
            #addressForm
            (focusout)="onAddressFormBlur(addressForm.addressForm)"
            [address]="shippingAddress$ | async"
            [availableCountries]="availableCountries$ | async"
            [hideCompany]="true"
        ></vsf-address-form>
        
        <div class="flex justify-end mt-4">
            <button (click)="setShippingAddress(addressForm.addressForm.value)" 
                    [disabled]="!addressForm.addressForm.valid"
                    class="checkout-shipping__button--save"
                    aria-label="Save shipping address">
                Save Shipping Address
            </button>
        </div>
    </div>

    <div class="bg-white p-6 rounded-[16px] border border-[var(--color-border-subtle)]" *ngIf="step === Step.Address">
        <h2 class="text-[18px] font-semibold text-black">Shipping Method</h2>
        <vsf-radio-card-fieldset
            [idFn]="getId"
            [selectedItemId]="shippingMethodId"
            (selectItem)="setShippingMethod($event.id)"
        >
            <div class="checkout-shipping__form-group--grid">
                <vsf-radio-card
                    *ngFor="let method of eligibleShippingMethods$ | async"
                    [item]="method"
                >
                    <div class="flex items-center">
                        <span class="text-black text-[18px] font-medium">
                            {{ method.name }}
                        </span>
                        <span
                            class="ml-4 text-[#263238] text-[14px] font-[400]"
                            *ngIf="method.priceWithTax > 0"
                        >
                            {{ method.priceWithTax | formatPrice }}
                        </span>
                    </div>
                </vsf-radio-card>
            </div>
        </vsf-radio-card-fieldset>
    </div>
</div>
